/**
 * topbar-timer.js — <countdown-bar> custom element.
 *
 * Two modes:
 *  - fixed:     counts down to a fixed instant (data-end, ISO 8601 with the shop
 *               tz offset) — same deadline for every visitor.
 *  - evergreen: counts down a duration (data-duration, seconds) and RESTARTS when
 *               it reaches 0. Persisted per visitor in localStorage (keyed by
 *               section + duration) so it continues across page loads, and rolls
 *               forward through any elapsed cycles.
 *
 * data-type      : "fixed" | "evergreen"
 * data-end       : ISO 8601 with offset (fixed mode)
 * data-duration  : seconds (evergreen mode)
 * data-key       : stable id for the localStorage key (evergreen mode)
 * data-expiry    : "zeros" | "hide"  (fixed mode only)
 * data-show-days : "true" | "false"
 *
 * Vanilla, guarded define, cleans up its interval on disconnect.
 */
if (!customElements.get('countdown-bar')) {
  customElements.define(
    'countdown-bar',
    class CountdownBar extends HTMLElement {
      connectedCallback() {
        this.type = this.getAttribute('data-type') || 'fixed';
        this.expiry = this.getAttribute('data-expiry') || 'zeros';
        this.showDays = this.getAttribute('data-show-days') === 'true';

        this.nums = {
          days: this.querySelector('[data-unit="days"] .topbar-timer__num'),
          hours: this.querySelector('[data-unit="hours"] .topbar-timer__num'),
          mins: this.querySelector('[data-unit="mins"] .topbar-timer__num'),
          secs: this.querySelector('[data-unit="secs"] .topbar-timer__num'),
        };

        if (this.type === 'evergreen') {
          this.duration = (parseInt(this.getAttribute('data-duration'), 10) || 0) * 1000;
          if (this.duration <= 0) {
            this.setAll(0);
            return;
          }
          this.storeKey = 'tbtimer:' + (this.getAttribute('data-key') || 'x') + ':' + this.duration;
          this.target = this.evergreenTarget();
        } else {
          this.target = Date.parse(this.getAttribute('data-end') || '');
          if (isNaN(this.target)) {
            this.setAll(0);
            return;
          }
        }

        this.tick = this.tick.bind(this);
        this.tick();
        this.timer = setInterval(this.tick, 1000);
      }

      disconnectedCallback() {
        if (this.timer) clearInterval(this.timer);
      }

      /** Evergreen: read a persisted deadline, rolling it forward past elapsed cycles;
       *  otherwise start a fresh one. Falls back to memory if storage is blocked. */
      evergreenTarget() {
        const now = Date.now();
        let deadline = NaN;
        try {
          deadline = parseInt(localStorage.getItem(this.storeKey), 10);
        } catch (e) {
          /* storage unavailable (private mode) — use a fresh in-memory cycle */
        }
        if (!deadline || isNaN(deadline)) {
          deadline = now + this.duration;
        } else {
          while (deadline <= now) deadline += this.duration;
        }
        this.persist(deadline);
        return deadline;
      }

      persist(value) {
        try {
          localStorage.setItem(this.storeKey, String(value));
        } catch (e) {
          /* ignore */
        }
      }

      tick() {
        let diff = this.target - Date.now();

        if (diff <= 0) {
          if (this.type === 'evergreen') {
            // Restart: roll the deadline forward by whole durations, then render.
            const now = Date.now();
            while (this.target <= now) this.target += this.duration;
            this.persist(this.target);
            diff = this.target - now;
          } else {
            if (this.timer) clearInterval(this.timer);
            if (this.expiry === 'hide') {
              this.style.display = 'none';
              this.setAttribute('hidden', '');
            } else {
              this.setAll(0);
            }
            this.dispatchEvent(new CustomEvent('countdown:ended', { bubbles: true }));
            return;
          }
        }

        const totalSec = Math.floor(diff / 1000);
        let days = Math.floor(totalSec / 86400);
        let hours = Math.floor((totalSec % 86400) / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        const secs = totalSec % 60;

        // When days aren't shown, roll them into the hours count.
        if (!this.showDays) {
          hours += days * 24;
          days = 0;
        }

        this.write(this.nums.days, days);
        this.write(this.nums.hours, hours);
        this.write(this.nums.mins, mins);
        this.write(this.nums.secs, secs);
      }

      write(el, value) {
        if (!el) return;
        const str = value < 10 ? '0' + value : String(value);
        if (el.textContent !== str) el.textContent = str;
      }

      setAll(value) {
        Object.values(this.nums).forEach((el) => this.write(el, value));
      }
    }
  );
}
